import * as THREE from 'three';
import { COLLISION } from '../constants/gameSettings.js';

export default class CollisionManager {
    constructor(scene, camera, audioManager, clock) {
        this.scene = scene;
        this.camera = camera;
        this.audioManager = audioManager;
        this.clock = clock;
        
        this.lastCollisionTime = 0;
        this.collisionCooldown = COLLISION.COOLDOWN;
        
        this.collisionSettings = {
            enabled: COLLISION.ENABLED,
            soundEnabled: COLLISION.SOUND_ENABLED,
            visualEnabled: COLLISION.VISUAL_ENABLED,
            minImpactForSound: COLLISION.MIN_IMPACT_FOR_SOUND,
            minImpactForVisual: COLLISION.MIN_IMPACT_FOR_VISUAL,
            shakeDuration: COLLISION.SHAKE_DURATION,
            shakeIntensity: COLLISION.SHAKE_INTENSITY
        };
        
        this.shakeParams = {
            active: false,
            startTime: 0,
            duration: 0,
            intensity: 0,
            originalCameraPosition: new THREE.Vector3()
        };

        this.isDisposed = false;
        this.onPhysicsCollision = this.handlePhysicsCollision.bind(this);
        this.activeParticleEffects = new Set();
    }
    
    setupCollisionHandling() {
        if (this.isDisposed) return;
        window.addEventListener('physics_collision', this.onPhysicsCollision);
    }

    handlePhysicsCollision(event) {
        if (this.isDisposed) return;
        this.handleCollision(event.detail);
    }
    
    handleCollision(collisionData) {
        const { bodyA, bodyB, position, normal, impactForce, impactSpeed } = collisionData;
        
        // Skip if collision effects are disabled
        if (!this.collisionSettings.enabled) return;
        
        // Skip if we're in collision cooldown period to prevent sound spam
        const now = performance.now();
        if (now - this.lastCollisionTime < this.collisionCooldown) return;
        this.lastCollisionTime = now;
        
        // Generate audio effect if enabled and impact is significant
        if (this.collisionSettings.soundEnabled && 
            impactForce > this.collisionSettings.minImpactForSound && 
            this.audioManager) {
            
            // Play collision sound at collision position
            this.audioManager.playCollisionSound(position, impactForce);
        }
        
        // Generate visual effect if enabled and impact is significant
        if (this.collisionSettings.visualEnabled && 
            impactForce > this.collisionSettings.minImpactForVisual) {
            
            // Create collision particles
            this.createCollisionParticles(position, normal, impactForce);
            
            // Apply camera shake effect based on impact force
            const shakeAmount = Math.min(
                this.collisionSettings.shakeIntensity,
                impactForce / 300 * this.collisionSettings.shakeIntensity
            );
            this.applyCameraShake(shakeAmount, this.collisionSettings.shakeDuration);
        }
    }
    
    // Create particle effect at collision point
    createCollisionParticles(position, normal, impactForce) {
        // Create particle group
        const particleCount = Math.min(50, Math.floor(impactForce / 10));
        if (particleCount <= 0) return;

        const particleGeometry = new THREE.BufferGeometry();
        const particlePositions = new Float32Array(particleCount * 3);
        const particleSizes = new Float32Array(particleCount);
        
        // Generate particles along the collision normal
        for (let i = 0; i < particleCount; i++) {
            // Position with some randomness
            const offset = new THREE.Vector3(
                (Math.random() - 0.5) * 1.0,
                (Math.random() - 0.5) * 1.0,
                (Math.random() - 0.5) * 1.0
            );
            
            // Add bias in the direction of the collision normal
            const normalBias = new THREE.Vector3().copy(normal).multiplyScalar(Math.random() * 0.5);
            offset.add(normalBias);
            
            // Set particle position
            const particlePos = new THREE.Vector3().copy(position).add(offset);
            particlePositions[i * 3] = particlePos.x;
            particlePositions[i * 3 + 1] = particlePos.y;
            particlePositions[i * 3 + 2] = particlePos.z;
            
            // Set particle size based on impact force
            particleSizes[i] = 0.1 + Math.random() * 0.3 * (impactForce / 100);
        }
        
        // Set geometry attributes
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        particleGeometry.setAttribute('size', new THREE.BufferAttribute(particleSizes, 1));
        
        // Create particle material
        const particleMaterial = new THREE.PointsMaterial({
            color: 0xAAAAAA,
            size: 0.2,
            transparent: true,
            opacity: 0.8,
            sizeAttenuation: true
        });
        
        // Create particle system
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        this.scene.add(particles);
        
        // Create animation for particles
        const duration = 1.0;
        const initialSizes = particleSizes.slice();
        const startTime = performance.now();
        let lastFrameTime = startTime;

        const cleanupParticles = () => {
            this.scene.remove(particles);
            particleGeometry.dispose();
            particleMaterial.dispose();
        };

        const effectState = { animationFrameId: null, cleanup: cleanupParticles };
        this.activeParticleEffects.add(effectState);
        
        // Store velocity for each particle
        const velocities = [];
        for (let i = 0; i < particleCount; i++) {
            // Velocity based on collision normal and randomness
            const vel = new THREE.Vector3(
                normal.x + (Math.random() - 0.5) * 2,
                normal.y + Math.random() * 2, // Bias upward
                normal.z + (Math.random() - 0.5) * 2
            );
            vel.normalize().multiplyScalar(2 + Math.random() * 3 * (impactForce / 100));
            velocities.push(vel);
        }
        
        // Animation loop for particles
        const animateParticles = (now) => {
            if (this.isDisposed) {
                cleanupParticles();
                this.activeParticleEffects.delete(effectState);
                return;
            }

            const elapsed = (now - startTime) / 1000;
            const deltaTime = Math.min((now - lastFrameTime) / 1000, 0.05);
            lastFrameTime = now;
            
            // Check if animation should end
            if (elapsed >= duration) {
                cleanupParticles();
                this.activeParticleEffects.delete(effectState);
                return;
            }
            
            // Update particle positions
            const positions = particleGeometry.attributes.position.array;
            const sizes = particleGeometry.attributes.size.array;
            
            for (let i = 0; i < particleCount; i++) {
                // Apply gravity and velocity
                const vel = velocities[i];
                vel.y -= 5 * deltaTime; // Apply gravity
                
                // Update position
                positions[i * 3] += vel.x * deltaTime;
                positions[i * 3 + 1] += vel.y * deltaTime;
                positions[i * 3 + 2] += vel.z * deltaTime;
                
                // Fade out size
                const fadeFactor = 1 - (elapsed / duration);
                sizes[i] = initialSizes[i] * Math.max(0, fadeFactor);
            }
            
            // Mark attributes for update
            particleGeometry.attributes.position.needsUpdate = true;
            particleGeometry.attributes.size.needsUpdate = true;
            
            // Continue animation
            effectState.animationFrameId = requestAnimationFrame(animateParticles);
        };
        
        // Start animation
        effectState.animationFrameId = requestAnimationFrame(animateParticles);
    }
    
    applyCameraShake(intensity, duration) {
        if (!this.camera) return;
        
        // Store original camera position
        this.shakeParams.originalCameraPosition.copy(this.camera.position);
        this.shakeParams.active = true;
        this.shakeParams.startTime = this.clock.getElapsedTime();
        this.shakeParams.duration = duration;
        this.shakeParams.intensity = intensity;
    }
    
    updateCameraShake() {
        if (!this.shakeParams.active || !this.camera) return;
        
        const currentTime = this.clock.getElapsedTime();
        const elapsed = currentTime - this.shakeParams.startTime;
        
        // Check if shake should end
        if (elapsed >= this.shakeParams.duration) {
            // Reset to original position
            this.shakeParams.active = false;
            return;
        }
        
        // Calculate shake factor (decay over time)
        const remainingFactor = 1 - (elapsed / this.shakeParams.duration);
        const shakeIntensity = this.shakeParams.intensity * remainingFactor;
        
        // Apply random offset to camera
        const offsetX = (Math.random() - 0.5) * shakeIntensity;
        const offsetY = (Math.random() - 0.5) * shakeIntensity;
        const offsetZ = (Math.random() - 0.5) * shakeIntensity;
        
        this.camera.position.x += offsetX;
        this.camera.position.y += offsetY;
        this.camera.position.z += offsetZ;
    }

    dispose() {
        this.isDisposed = true;
        window.removeEventListener('physics_collision', this.onPhysicsCollision);

        for (const effect of this.activeParticleEffects) {
            if (effect.animationFrameId !== null) {
                cancelAnimationFrame(effect.animationFrameId);
            }
            if (typeof effect.cleanup === 'function') {
                effect.cleanup();
            }
        }
        this.activeParticleEffects.clear();
    }
} 
